import { GeneralError, GeneralErrorCause } from '51trips-frontend-core'
import { enqueueSnackbar } from 'notistack'

import i18next from '../i18n'

/**
 * Firebase Auth codes we can describe more usefully than the
 * `GeneralErrorCause` they map to. `GeneralErrorCause` lives in
 * 51trips-frontend-core, so until dedicated causes are added there, these two
 * are resolved from the underlying Firebase code instead: the account conflict
 * would otherwise read as a plain "email already in use" with no way forward,
 * and a blocked popup would tell the user to try again later when what they
 * need is to allow the popup.
 */
function messageForFirebaseAuthCode(errorCode: string): string | undefined {
  switch (errorCode) {
    case 'auth/account-exists-with-different-credential':
      return i18next.t('errors:errors.accountExistsWithOtherProvider')
    case 'auth/popup-blocked':
      return i18next.t('errors:errors.popupBlocked')
    default:
      return undefined
  }
}

function generateErrorMessage(error: GeneralError): string {
  switch (error.cause) {
    case GeneralErrorCause.networkConnection:
      return i18next.t('errors:errors.networkSettings')
    case GeneralErrorCause.forbidden:
      return i18next.t('errors:errors.maxLogInAttempts')
    case GeneralErrorCause.invalidEmail:
      return i18next.t('errors:errors.invalidEmail')
    case GeneralErrorCause.invalidCredentials:
      return i18next.t('errors:errors.invalidCredentials')
    case GeneralErrorCause.emailAlreadyInUse:
      return i18next.t('errors:errors.emailAlreadyExists')
    case GeneralErrorCause.weakPassword:
      return i18next.t('errors:errors.weakPassword')
    case GeneralErrorCause.wrongPassword:
      return i18next.t('errors:errors.incorrectPassword')
    case GeneralErrorCause.userNotFound:
      return i18next.t('errors:errors.userNotFound')
    case GeneralErrorCause.requestCancelled:
      return i18next.t('errors:errors.operationCancelled')
    case GeneralErrorCause.userCancelledOperation:
      return i18next.t('errors:errors.userCancelledOperation')
    case GeneralErrorCause.unknown:
      return i18next.t('errors:errors.errorUnknown')
    case GeneralErrorCause.invalidArgument:
      return i18next.t('errors:errors.badRequest')
    case GeneralErrorCause.deadlineExceeded:
      return i18next.t('errors:errors.operationCancelled')
    case GeneralErrorCause.dataNotFound:
      return i18next.t('errors:errors.fileNotFound')
    case GeneralErrorCause.alreadyExists:
      return i18next.t('errors:errors.fileAlreadyExists')
    case GeneralErrorCause.permissionDenied:
      return i18next.t('errors:errors.missingPermissions')
    case GeneralErrorCause.resourceExhausted:
      return i18next.t('errors:errors.exceededOperationsPermitted')
    case GeneralErrorCause.failedPrecondition:
      return i18next.t('errors:errors.operationRejected')
    case GeneralErrorCause.aborted:
      return i18next.t('errors:errors.operationAborted')
    case GeneralErrorCause.outOfRange:
      return i18next.t('errors:errors.operationCancelled')
    case GeneralErrorCause.unimplemented:
      return i18next.t('errors:errors.operationImplementationFail')
    case GeneralErrorCause.internalServerError:
    case GeneralErrorCause.unavailable:
      return i18next.t('errors:errors.serverError')
    case GeneralErrorCause.dataLoss:
      return i18next.t('errors:errors.corruptData')
    case GeneralErrorCause.unauthenticated:
      return i18next.t('errors:errors.logInRequired')
    case GeneralErrorCause.badRequest:
      return i18next.t('errors:errors.serverError')
    case GeneralErrorCause.invalidSearchQuery:
      return i18next.t('errors:errors.invalidSearchQuery')
    case GeneralErrorCause.invalidImageRouteData:
      return i18next.t('errors:errors.imageRouteData')
    case GeneralErrorCause.unknownImagePickerErrorCode:
      return i18next.t('errors:errors.unknownImagePickerErrorCode')
    case GeneralErrorCause.dynamicLinkinitialLinkError:
      return i18next.t('errors:errors.serviceUnavailableOnDevice')
    case GeneralErrorCause.dynamicLinkBuildFailed:
      return i18next.t('errors:errors.linkCreationFailed')
    case GeneralErrorCause.dynamicLinkNotFound:
      return i18next.t('errors:errors.linkNotFound')
    case GeneralErrorCause.googleLogInNotPossible:
      return i18next.t('errors:errors.logInNotPossible')
    case GeneralErrorCause.googleInvalidCredential:
      return i18next.t('errors:errors.invalidCredentials')
    case GeneralErrorCause.googleLogInPendingCompletion:
      return i18next.t('errors:errors.logInPendingCompletion')
    case GeneralErrorCause.googleRequestCancelled:
      return i18next.t('errors:errors.googleAuthCancelled')
    case GeneralErrorCause.googleUserNotLoggedIn:
      return i18next.t('errors:errors.userNotLoggedIn')
    case GeneralErrorCause.googleUserAlreadyLoggedIn:
      return i18next.t('errors:errors.userLoggedIn')
    case GeneralErrorCause.stripeInvalidPromoCode:
      return i18next.t('errors:errors.promoCode')
    case GeneralErrorCause.stripeInvalidCredentials:
      return i18next.t('errors:errors.invalidCredentials')
    case GeneralErrorCause.agencyNotFound:
      return i18next.t('errors:errors.agencyNotFound')
    case GeneralErrorCause.chatGptCallFailed:
      return i18next.t('errors:errors.generateSummary')
    case GeneralErrorCause.remoteConfigLoadFailed:
      return i18next.t('errors:errors.fetchRemoteConfig')
    default:
      return i18next.t('errors:errors.generalOops')
  }
}

/**
 * A TripBook frozen for review answers every edit with 423 and a
 * `tripBookInReview` reason. The editor funnels all of its save failures
 * through this function, so recognising the shape here is what turns a generic
 * "algo no está bien" into an explanation the user can act on — and it covers
 * any editing path whose UI affordance we failed to hide.
 */
function tripBookInReviewMessage(error: unknown): string | undefined {
  const response = (error as { response?: { status?: number; data?: { reason?: unknown } } })
    ?.response
  if (response?.status !== 423) return undefined
  if (response.data?.reason !== 'tripBookInReview') return undefined
  return i18next.t('errors:errors.tripBookInReview')
}

const describeError = (value: unknown): string | undefined => {
  if (value === null || value === undefined) return undefined
  if (typeof value === 'string') return value
  if (value instanceof GeneralError) {
    // `contextDescription` is private in the core package, but it is the only
    // human-readable part; `message` would inline the whole underlying stack.
    const context = (value as unknown as { contextDescription?: string }).contextDescription
    return [context?.replace(/[.\s]+$/, ''), describeError(value.underlyingError)]
      .filter(Boolean)
      .join(': ')
  }
  const { name, message } = value as { name?: unknown; message?: unknown }
  if (typeof message === 'string') {
    return typeof name === 'string' && name && name !== 'Error' ? `${name}: ${message}` : message
  }
  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}

const innermostStack = (value: unknown): string | undefined => {
  if (value instanceof GeneralError) return innermostStack(value.underlyingError)
  const stack = (value as { stack?: unknown } | null | undefined)?.stack
  return typeof stack === 'string' ? stack : undefined
}

/**
 * Turns anything caught into a real `Error` to hand to `console.error`.
 * Sentry's captureConsole integration only reports an `Error` instance as an
 * exception (readable title + stack); anything else is stringified, which is
 * how `GeneralError` (it implements `Error` without extending it) and plain
 * objects end up as "[object Object]" issues.
 */
export function toLoggableError(error: unknown, context?: string): Error {
  if (error instanceof Error && !context) return error
  const loggable = new Error(
    [context, describeError(error)].filter(Boolean).join(': ') || 'Unknown error'
  )
  const stack = innermostStack(error)
  if (stack) loggable.stack = stack
  return loggable
}

export function userReadableDescription(error: GeneralError | string) {
  console.error(typeof error === 'string' ? error : toLoggableError(error))

  const inReview = tripBookInReviewMessage(error)
  if (inReview) {
    enqueueSnackbar(inReview, { variant: 'warning' })
    return
  }

  if (typeof error === 'string') {
    enqueueSnackbar(error, { variant: 'error' })
    return
  }

  const errorCode = (error.underlyingError as { code?: unknown } | undefined)?.code
  const specificMessage =
    typeof errorCode === 'string' ? messageForFirebaseAuthCode(errorCode) : undefined

  enqueueSnackbar(specificMessage ?? generateErrorMessage(error), { variant: 'error' })
}
